import { type TFunction } from 'i18next';
import { type RecurrenceRule, addCalendarDays, scheduledDates } from '@apm/contracts';

/**
 * Các lần **theo lịch** kế tiếp tính từ `from` — ô "3 lần tới" trong form mẫu
 * việc định kỳ (APM-REC-b).
 *
 * Chưa dời ngày nghỉ: việc đó cần lịch lễ của tổ chức, thứ form không có. Nhãn
 * dưới ô xem trước nói điều này ra, để một lần rơi vào Chủ nhật không trông như
 * lỗi.
 *
 * Dò tối đa 400 ngày — cùng trần với `RecurrenceService.nextDate` ở API, nên
 * một lịch "ngày 31 hằng tháng" vẫn ra đủ ba lần.
 */
export function previewDates(rule: RecurrenceRule, from: string, count = 3): string[] {
  const start = from > rule.startDate ? from : rule.startDate;
  return scheduledDates(rule, start, addCalendarDays(start, 400)).slice(0, count);
}

/** Thứ tự hiện các thứ trong tuần: thứ Hai trước, Chủ nhật cuối — như lịch ở Việt Nam. */
export const WEEKDAY_ORDER = [1, 2, 3, 4, 5, 6, 0] as const;

/** "Hằng tuần: T2, T5", "Ngày cuối tháng", "Mỗi 14 ngày"… */
export function ruleSummary(rule: RecurrenceRule, t: TFunction): string {
  switch (rule.freq) {
    case 'daily':
      return t('recurrence.summary.daily');
    case 'weekly':
      return t('recurrence.summary.weekly', {
        days: WEEKDAY_ORDER.filter((day) => rule.weekdays.includes(day))
          .map((day) => t(`recurrence.weekdayShort.${day}`))
          .join(', '),
      });
    case 'monthly':
      return rule.monthDay === 0 || rule.monthDay === null
        ? t('recurrence.summary.monthlyLast')
        : t('recurrence.summary.monthly', { day: rule.monthDay });
    case 'interval':
      return t('recurrence.summary.interval', { count: rule.intervalDays ?? 0 });
  }
}
